import {
    fetchBaseQuery,
    type BaseQueryApi,
    type BaseQueryFn,
    type FetchArgs,
    type FetchBaseQueryError,
    type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import { getApiUrl} from "../config";
import type {RootState} from "./store";
import {recordAccountBlock} from '../services/accountAccessState';
import { logError } from '../utils/loggerHelpers';
import {
    ensureFreshAccessToken,
    expireSession,
    getAccessToken,
    refreshAccessToken,
    runWithAuthRetry,
} from '../services/authRuntime';

type BaseQueryDependencies = {
    getBaseUrl: () => Promise<string>;
    getToken: () => string | undefined;
    refreshToken: () => Promise<boolean>;
    onSessionExpired: () => Promise<void>;
    ensureFreshToken?: () => Promise<void>;
};

const authNotReadyError = (): {error: FetchBaseQueryError} => ({
    error: {
        status: 'CUSTOM_ERROR',
        error: 'Authentication is not ready',
    },
});

export const createBaseQuery = ({
    getBaseUrl,
    getToken,
    refreshToken,
    onSessionExpired,
    ensureFreshToken,
}: BaseQueryDependencies): BaseQueryFn<unknown, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta> =>
    async (args, api: BaseQueryApi, extraOptions) => {
        const baseUrl = await getBaseUrl();
        const state = api.getState() as RootState;
        const tenant = state.auth?.tenant;
        const deviceId = state.auth?.deviceId;

        if (!getToken()) {
            logError('Blocked API request before authentication was ready', {
                source: 'api',
                event: 'auth_not_ready',
                url: baseUrl,
            });
            return authNotReadyError();
        }

        await ensureFreshToken?.();

        const rawBaseQuery = fetchBaseQuery({
            baseUrl,
            prepareHeaders: (headers) => {
                const token = getToken();
                if (token) {
                    headers.set('Authorization', `Bearer ${token}`);
                }
                if (tenant) {
                    headers.set('tenant', tenant);
                } else {
                    logError('API request without tenant header', {
                        source: 'api',
                        event: 'tenant_missing',
                        url: baseUrl,
                    });
                }
                if (deviceId) {
                    headers.set('X-Device-Id', deviceId);
                }
                return headers;
            },
        });

        const result = await runWithAuthRetry({
            execute: async () => rawBaseQuery(args as string | FetchArgs, api, extraOptions),
            isUnauthorized: (queryResult) => queryResult.error?.status === 401,
            refresh: refreshToken,
            onSessionExpired,
        });
        recordAccountBlock(result.error);
        return result;
    };

export const baseQuery = createBaseQuery({
    getBaseUrl: async () => await getApiUrl() as string,
    getToken: getAccessToken,
    refreshToken: refreshAccessToken,
    onSessionExpired: expireSession,
    ensureFreshToken: ensureFreshAccessToken,
});
